import _ from 'lodash';
import {keys} from 'ts-transformer-keys';

import {__} from '~/shared/src/utilities/messageCatalog';
import User, {View} from '~/shared/src/model/User';
import {
    buildItemLabelForCypher,
    fieldSeparator,
    DataStructure,
    aliasSeparator,
    getItemLabelForCypher
} from '~/shared/src/utilities/dataStructure';
import dataUtilities from '~/shared/src/utilities/dataUtils';
import {
    ColumnConfig,
    ColumnDefinition,
    RowStyling
} from '~/shared/src/validationSchemes/modules/overview';
import {
    getEnumPropertyColumnDefinitions,
    getInitialSorters,
    getItemAttributeColumnDefinitions,
    getPropertyColumnDefinitions,
    getReferenceColumnDefinitions
} from '~/shared/src/utilities/modules/finder';
import {
    getDataStructure,
    getColumnDefinitions,
    getParentReferenceAttributeColumnDefinitions,
    getParentReferenceMetaDataColumnDefinitions
} from '~/shared/src/utilities/modules/finderReferencePanel';

import {
    getColumnConfigFromColumnDefinition,
    getColumnHeader
} from '~/frontend/src/components/base/DataTable/utilities';
import {getModuleForViewSelector as getModuleForViewSelectorOfOverview} from '~/frontend/src/components/base/ItemFinder/overview/utilities';
import ReferencePanel, {
    defaultProps as referencePanelDefaultProps,
    DefaultProps as ReferencePanelDefaultProps,
    ComponentProps as ReferencePanelComponentProps
} from '~/frontend/src/components/base/ItemFinder/detail/ReferencePanel/index';
import {PanelProps as ViewSelectorDropdownPanelProps} from '~/frontend/src/components/base/shared/field/ViewSelectorDropdown';
import {
    ColumnSortMenuColumnsGroup,
    ColumnSortMenuSingleColumn
} from '~/frontend/src/components/base/shared/menu/ColumnSortMenu';


export const preInitialize = ( props : ReferencePanelComponentProps & { user : User } ) : ReferencePanelComponentProps => {
    const retVal : ReferencePanelComponentProps & { user? : User } = {
        ...props
    };

    delete retVal.user;

    // set key for columns, filters and sorters if not
    if ( retVal.columns?.length ) {
        for ( let i = retVal.columns.length - 1; i >= 0; i-- ) {
            if ( !retVal.columns[ i ].key ) {
                retVal.columns[ i ].key = retVal.columns[ i ].field;
            }
        }
    }

    if ( retVal.filters?.length ) {
        for ( let i = retVal.filters.length - 1; i >= 0; i-- ) {
            if ( !retVal.filters[ i ].key ) {
                retVal.filters[ i ].key = retVal.filters[ i ].field;
            }
        }
    }

    if ( retVal.sorters?.length ) {
        for ( let i = retVal.sorters.length - 1; i >= 0; i-- ) {
            if ( !retVal.sorters[ i ].key ) {
                retVal.sorters[ i ].key = retVal.sorters[ i ].field;
            }
        }
    }

    return retVal;
}

export const initialize = ( props : ReferencePanelComponentProps & { user : User } ) : ReferencePanelComponentProps => {
    let retVal = {
        ...referencePanelDefaultProps,
        ...props,
        panelTransformer: 'referencePanel'
    };

    const dataStructure = getDataStructure( retVal.schema, retVal.itemTypeName, retVal.parentItemTypeName, retVal.user );

    // no columns in state -> initialize
    if ( !retVal.columns.length ) {
        let defaults = getDefaultViewProps( retVal.schema, retVal.itemTypeName, retVal.parentItemTypeName, dataStructure, props.user );
        if ( Object.keys( defaults ).length ) {
            defaults = buildPropsFromViewProps( defaults, dataStructure, retVal.schema, retVal.status, retVal.timestamp, retVal.itemTypeName, props.user )
        }

        if ( Object.keys( defaults ).length ) {
            retVal = {
                ...retVal,
                ...defaults
            };
        }
        else if ( dataStructure.dataStructure?.[ 0 ] ) {
            retVal.sorters = getInitialSorters( dataStructure.dataStructure[ 0 ], retVal.schema, retVal.itemTypeName );
        }

        retVal.columnDefinitions = getColumnDefinitions( dataStructure, retVal.schema, retVal.status, retVal.timestamp, retVal.itemTypeName, props.user );

        // check columns against columnDefinitions
        if ( retVal.columns.length ) {
            for ( let i = retVal.columns.length - 1; i >= 0; i-- ) {
                if ( !retVal.columnDefinitions.find( colDef => colDef.field === retVal.columns[ i ].field ) ) {
                    retVal.columns.splice( i, 1 );
                }
            }
        }

        // no columns -> initial
        if ( !retVal.columns.length ) {
            retVal.columns = getInitialColumns( dataStructure, retVal.schema, retVal.status, retVal.timestamp, retVal.itemTypeName, retVal.parentItemTypeName, props.user );
            retVal.forceFit = true;
        }
    }

    // check columns not in columnDefinitions (mostly metaData columns not for current targetArea)
    else {
        retVal.columnDefinitions = getColumnDefinitions( dataStructure, retVal.schema, retVal.status, retVal.timestamp, retVal.itemTypeName, props.user );

        for ( let i = retVal.columns.length - 1; i >= 0; i-- ) {
            const columnDefinition = retVal.columnDefinitions.find( colDef => colDef.field === retVal.columns[ i ].field );

            if ( !columnDefinition ) {
                retVal.columns = ( [] as ColumnConfig[] ).concat( retVal.columns );
                retVal.columns.splice( i, 1 );
            }
        }
    }

    retVal.dataStructure = dataStructure;

    // remove all non allowed props
    // remove if equals default
    const allowedPropKeys = keys<ReferencePanelComponentProps>() as string[],
          propKeys        = Object.keys( retVal ) as ( keyof ReferencePanelDefaultProps )[];

    for ( const propKey of propKeys ) {
        if (
            !allowedPropKeys.includes( propKey )
            || JSON.stringify( retVal[ propKey ] ) === JSON.stringify( referencePanelDefaultProps[ propKey ] )
        ) {
            delete retVal[ propKey ];
        }
    }

    return retVal;
}

/**
 * Builds the initial columns
 *
 * @param {DataStructure} dataStructure
 * @param {String} schema
 * @param {Status} status
 * @param {String} timestamp
 * @param {String} itemTypeName
 * @param {String} parentItemTypeName
 * @param {User} user
 *
 * @return {ColumnConfig[]}
 */
export const getInitialColumns = ( dataStructure : DataStructure, schema : Schema, status : Status, timestamp : string, itemTypeName : string, parentItemTypeName : string, user : User ) : ColumnConfig[] => {
    const defaultProps     = getDefaultViewProps( schema, itemTypeName, parentItemTypeName, dataStructure, user ),
          defaultShowCount = 6;

    let retVal : ColumnConfig[] = [];

    // get columns from default view if
    if ( defaultProps.columns?.length ) {
        retVal = defaultProps.columns;
    }
    else {
        const columnDefinitions = getColumnDefinitions( dataStructure, schema, status, timestamp, itemTypeName, user );

        for ( let i = 0; i < columnDefinitions.length, i < defaultShowCount; i++ ) {
            retVal.push( getColumnConfigFromColumnDefinition( columnDefinitions[ i ] ) );
        }
    }

    return retVal;
}

/**
 * Gets the module for viewSelector.
 *
 * @param {Schema} schema
 * @param {string} itemTypeName
 * @param {string} parentItemTypeName
 */
export const getModuleForViewSelector = ( schema : Schema, itemTypeName : string, parentItemTypeName : string ) : string[] => {
    return [
        ReferencePanel.viewSelectorModule,
        schema,
        parentItemTypeName,
        itemTypeName
    ];
}

/**
 * Gets the views from user.
 *
 * @param {Schema} schema
 * @param {string} itemTypeName
 * @param {string} parentItemTypeName
 * @param {DataStructure} dataStructure
 * @param {User} user
 *
 * @return {View[]}
 */
export const getViews = ( schema : Schema, itemTypeName : string, parentItemTypeName : string, dataStructure : DataStructure, user : User ) : View[] => {
    const
        // get reference views just for this parent ItemType
        retVal         = user.getViewsForModule( getModuleForViewSelector( schema, itemTypeName, parentItemTypeName ) ) ?? [],
        // get other reference views for all other parent ItemTypes
        referenceViews = user.getViewsForModule( [
            ReferencePanel.viewSelectorModule,
            schema,
            `(?!.*(${parentItemTypeName})).*`,
            itemTypeName
        ] ) ?? [],
        // get overview views
        overviewViews  = user.getViewsForModule( getModuleForViewSelectorOfOverview( schema, itemTypeName ) ) ?? [],
        viewsLabels    = retVal.map( view => view.label );

    let viewsHaveDefault = retVal.reduce( ( innerRetVal, view ) => innerRetVal || view.panelProps.default, false );

    // add other reference views (only label does not exist in views and remove default if there is still one)
    for ( const view of referenceViews ) {
        if ( !viewsLabels.includes( view.label ) ) {
            if ( viewsHaveDefault ) {
                delete view.panelProps.default;
            }
            else if ( view.panelProps.default ) {
                viewsHaveDefault = true;
            }

            retVal.push( view );
        }
    }

    // add overview views (only label does not exist in views and remove default if there is still one)
    for ( const view of overviewViews ) {
        if ( !viewsLabels.includes( view.label ) ) {
            if ( viewsHaveDefault ) {
                delete view.panelProps.default;
            }
            else if ( view.panelProps.default ) {
                viewsHaveDefault = true;
            }

            if ( dataStructure.dataStructure?.[ 0 ] ) {
                const dataObject = dataStructure.dataStructure[ 0 ];

                if ( view.panelProps.columns?.length ) {
                    view.panelProps.columns = remapFieldsForReferenceView( view.panelProps.columns, dataObject );
                    view.panelProps.columns = addParentReferenceAttributeColumnsToColumns( view.panelProps.columns, dataObject );
                }

                if ( view.panelProps.rowStyling ) {
                    view.panelProps.rowStyling = remapFieldsForReferenceView( view.panelProps.rowStyling, dataObject ) as RowStyling[];
                }
                if ( view.panelProps.fixFilters?.length ) {
                    view.panelProps.fixFilters = remapFieldsForReferenceView( view.panelProps.fixFilters, dataObject );
                }
                if ( view.panelProps.filters?.length ) {
                    view.panelProps.filters = remapFieldsForReferenceView( view.panelProps.filters, dataObject );
                }
                if ( view.panelProps.sorters?.length ) {
                    view.panelProps.sorters = remapFieldsForReferenceView( view.panelProps.sorters, dataObject );
                }
            }

            retVal.push( view );
        }
    }

    // set key for columns, filters and sorters if not
    for ( let viewIndex = 0; viewIndex < retVal.length; viewIndex++ ) {
        if ( retVal[ viewIndex ].panelProps.columns?.length ) {
            for ( let i = retVal[ viewIndex ].panelProps.columns.length - 1; i >= 0; i-- ) {
                if ( !retVal[ viewIndex ].panelProps.columns[ i ].key ) {
                    retVal[ viewIndex ].panelProps.columns[ i ].key = retVal[ viewIndex ].panelProps.columns[ i ].field;
                }
            }
        }

        if ( retVal[ viewIndex ].panelProps.filters?.length ) {
            for ( let i = retVal[ viewIndex ].panelProps.filters.length - 1; i >= 0; i-- ) {
                if ( !retVal[ viewIndex ].panelProps.filters[ i ].key ) {
                    retVal[ viewIndex ].panelProps.filters[ i ].key = retVal[ viewIndex ].panelProps.filters[ i ].field;
                }
            }
        }

        if ( retVal[ viewIndex ].panelProps.sorters?.length ) {
            for ( let i = retVal[ viewIndex ].panelProps.sorters.length - 1; i >= 0; i-- ) {
                if ( !retVal[ viewIndex ].panelProps.sorters[ i ].key ) {
                    retVal[ viewIndex ].panelProps.sorters[ i ].key = retVal[ viewIndex ].panelProps.sorters[ i ].field;
                }
            }
        }
    }

    // reorder by label
    retVal.sort( ( val1, val2 ) => {
        if ( val1.label > val2.label ) {
            return 1;
        }
        else if ( val1.label < val2.label ) {
            return -1;
        }
        else {
            return 0;
        }
    } );

    return retVal;
}

/**
 * Gets the default view for given parameters from the given user if there are some.
 *
 * @param {Schema} schema
 * @param {string} itemTypeName
 * @param {string} parentItemTypeName
 * @param {DataStructure} dataStructure
 * @param {User} user
 *
 * @return {Partial<OverviewComponentProps>}
 */
export const getDefaultViewProps = ( schema : Schema, itemTypeName : string, parentItemTypeName : string, dataStructure : DataStructure, user : User ) : Partial<ReferencePanelComponentProps> => {
    const views = getViews( schema, itemTypeName, parentItemTypeName, dataStructure, user );

    let retVal : Partial<ReferencePanelComponentProps> = {};

    for ( const view of views ) {
        if ( view.panelProps.default ) {
            retVal = view.panelProps;
            break;
        }
    }

    return retVal;
}

/**
 * Build the full set of props to set for given viewProps.
 *
 * @param {Partial<ReferencePanelComponentProps>} props
 * @param {DataStructure} dataStructure
 * @param {String} schema
 * @param {Status} status
 * @param {String} timestamp
 * @param {String} itemTypeName
 * @param {User} user
 * @param {( keyof ReferencePanelComponentProps )[]} excludePropKeys
 *
 * @return {Partial<ReferencePanelDefaultProps>}
 */
export const buildPropsFromViewProps = ( props : Partial<ReferencePanelDefaultProps>, dataStructure : DataStructure, schema : Schema, status : Status, timestamp : string, itemTypeName : string, user : User, excludePropKeys : ( keyof ReferencePanelComponentProps )[] = [] ) : Partial<ReferencePanelDefaultProps> => {
    props = {
        ...referencePanelDefaultProps,
        ...props
    };

    const retVal                 = {} as Partial<ReferencePanelDefaultProps>,
          propKeys               = Object.keys( props ) as ( keyof ReferencePanelDefaultProps )[],
          allowedPropKeys        = keys<ReferencePanelDefaultProps>() as ( keyof ReferencePanelComponentProps )[],
          defaultExcludePropKeys = [
              'schema',             // defined by component
              'itemTypeName',       // defined by component
              'parentItemId',       // defined by component
              'parentItemName',     // defined by component
              'dataStructure',      // computed
              'columnDefinitions',  // computed
              'actionColumns',      // computed
              'editable',           // computed
              'className',          // defined by component
              'status',             // defined by outer component
              'timestamp',          // defined by outer component
              'timezone',           // defined by outer component
              'searchString',       // defined by outer component
              'search'              // defined by outer component
          ] as ( keyof ReferencePanelComponentProps )[];

    // allowed prop (only ReferencePanelDefaultProps are allowed)
    // no default exclude prop
    // no given exclude prop
    for ( const propKey of propKeys ) {
        if (
            allowedPropKeys.includes( propKey )
            && !defaultExcludePropKeys.includes( propKey )
            && !excludePropKeys.includes( propKey )
        ) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            retVal[ propKey ] = props[ propKey ];
        }
    }

    // add columnDefinition
    retVal.columnDefinitions = getColumnDefinitions( dataStructure, schema, status, timestamp, itemTypeName, user );

    retVal.doSearch = retVal.search;

    return retVal;
}

/**
 * Extracts the minimal viewProps from given props.
 *
 * @param {ReferencePanelComponentProps} props
 * @param {( keyof ReferencePanelComponentProps )[]} excludePropKeys
 *
 * @return {Partial<ReferencePanelComponentProps>}
 */
export const extractViewPropsFromProps = ( props : ReferencePanelComponentProps, excludePropKeys : ( keyof ReferencePanelComponentProps )[] = [] ) : Partial<ReferencePanelComponentProps> => {
    const retVal                 = {} as Partial<ReferencePanelComponentProps>,
          propKeys               = Object.keys( props ) as ( keyof ReferencePanelComponentProps )[],
          allowedPropKeys        = keys<ReferencePanelDefaultProps>() as string[],
          defaultExcludePropKeys = [
              'schema',             // defined by component
              'itemTypeName',       // defined by component
              'parentItemId',       // defined by component
              'parentItemName',     // defined by component
              'dataStructure',      // computed
              'columnDefinitions',  // computed
              'actionColumns',      // computed
              'editable',           // computed
              'forceFit',           // will be always false
              'autoLayout',         // will be always false
              'className',          // defined by component
              'status',             // defined by outer component
              'timestamp',          // defined by outer component
              'timezone',           // defined by outer component
              'searchString',       // defined by outer component
              'search'              // defined by outer component
          ] as ( keyof ReferencePanelComponentProps )[];

    // allowed prop (only ReferencePanelDefaultProps are allowed)
    // no default exclude prop
    // no given exclude prop
    // not equals default
    for ( const propKey of propKeys ) {
        if (
            allowedPropKeys.includes( propKey )
            && !defaultExcludePropKeys.includes( propKey )
            && !excludePropKeys.includes( propKey )
            && JSON.stringify( props[ propKey ] ) !== JSON.stringify( referencePanelDefaultProps[ propKey as keyof ReferencePanelDefaultProps ] )
        ) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            retVal[ propKey ] = props[ propKey ];
        }
    }

    return retVal;
}

/**
 * Builds the grouped columns with visibility.
 *
 * @param {ColumnDefinition[]} columnDefinitions
 * @param {ColumnConfig[]} columns
 * @param {DataStructure} dataStructure
 * @param {Schema} schema
 * @param {Status} status
 * @param {string} timestamp
 * @param {number} itemTypeName
 * @param {boolean} rawMode
 * @param {boolean} showAdditionalRawNames
 * @param {User} user
 *
 * @return {ColumnSortMenuColumnsGroup[]}
 */
export const getAllColumns = ( columnDefinitions : ColumnDefinition[], columns : ColumnConfig[], dataStructure : DataStructure, schema : Schema, status : Status, timestamp : string, itemTypeName : string, rawMode : boolean, showAdditionalRawNames : boolean, user : User ) : ColumnSortMenuColumnsGroup[] => {
    const retVal : ColumnSortMenuColumnsGroup[] = [],
          groupColumnExtractor                  = ( groupColumnFields : string[] ) : ColumnSortMenuSingleColumn[] => {
              const innerRetVal : ColumnSortMenuSingleColumn[] = [];

              for ( const columnDefinition of columnDefinitions ) {
                  if ( groupColumnFields.includes( columnDefinition.field ) ) {
                      const column = columns.find( colConfig => colConfig.field === columnDefinition.field );

                      innerRetVal.push( {
                          key      : columnDefinition.key,
                          field    : columnDefinition.field,
                          header   : getColumnHeader( columnDefinition, dataStructure, schema, rawMode, showAdditionalRawNames ),
                          hidden   : !column,
                          hidable  : columnDefinition.hidable ?? true,
                          valueType: dataUtilities.getDataValueType( columnDefinition.dataDescriptor, schema, dataStructure ) ?? 'STRING'
                      } );
                  }
              }

              return innerRetVal;
          };

    if ( dataStructure.dataStructure?.length === 1 ) {
        const dataObject = dataStructure.dataStructure[ 0 ];

        // parentReference and item attribute columns
        retVal.push( {
            key    : 'showAttributes',
            label  : __( 'YN.base.finder.attributes.label' ),
            columns: groupColumnExtractor( getParentReferenceAttributeColumnDefinitions( dataObject ).map( colDef => colDef.field ) ).concat(
                groupColumnExtractor( getItemAttributeColumnDefinitions( dataObject ).map( colDef => colDef.field ) )
            )
        } );

        // property columns
        retVal.push( {
            key    : 'showProperties',
            label  : __( 'YN.base.finder.properties.label' ),
            columns: groupColumnExtractor( getPropertyColumnDefinitions( dataObject, schema, itemTypeName, user ).map( colDef => colDef.field ) )
        } );

        // enumerated property columns
        retVal.push( {
            key    : 'showEnumProperties',
            label  : __( 'YN.base.finder.enumProperties.label' ),
            columns: groupColumnExtractor( getEnumPropertyColumnDefinitions( dataObject, schema, itemTypeName, user ).map( colDef => colDef.field ) )
        } );

        // reference columns
        retVal.push( {
            key    : 'showReferences',
            label  : __( 'YN.base.finder.references.label' ),
            columns: groupColumnExtractor( getReferenceColumnDefinitions( dataObject, schema, itemTypeName, user ).map( colDef => colDef.field ) )
        } );

        // metaData columns
        retVal.push( {
            key    : 'showMetaData',
            label  : __( 'YN.base.finder.metaData.label' ),
            columns: groupColumnExtractor( getParentReferenceMetaDataColumnDefinitions( dataObject, status, timestamp ).map( colDef => colDef.field ) )
        } );
    }

    return retVal;
}

/**
 * Adds the parent reference attribute columns if there is no one of them in given columns
 *
 * @param {ColumnConfig[]} columns
 * @param {DataStructure} dataStructure
 *
 * @return {ColumnConfig[]}
 */
export const addParentReferenceAttributeColumnsToColumns = ( columns : ColumnConfig[], dataStructure : DataStructure ) : ColumnConfig[] => {
    const retVal : ColumnConfig[]          = ( [] as ColumnConfig[] ).concat( columns ),
          parentReferenceColumnDefinitions = getParentReferenceAttributeColumnDefinitions( dataStructure ),
          parentReferenceColumnFields      = parentReferenceColumnDefinitions.map( colDef => colDef.field );

    // take care for parentReference columns
    if ( retVal.length ) {
        let parentReferenceColumnFound = false;

        for ( const column of retVal ) {
            if ( parentReferenceColumnFields.includes( column.field ) ) {
                parentReferenceColumnFound = true;
                break;
            }
        }

        // if there is no one add them all
        if ( !parentReferenceColumnFound ) {
            for ( let i = parentReferenceColumnDefinitions.length - 1; i >= 0; i-- ) {
                retVal.unshift( getColumnConfigFromColumnDefinition( parentReferenceColumnDefinitions[ i ] ) );
            }
        }
    }

    return retVal;
}

/**
 * Compares the given panelProps with the given viewPanelProps.
 *
 * @param {ViewSelectorDropdownPanelProps} panelProps
 * @param {ViewSelectorDropdownPanelProps} viewPanelProps
 *
 * @return {boolean}
 *
 * @private
 */
export const viewSelectorPanelPropsCompare = ( panelProps : ViewSelectorDropdownPanelProps, viewPanelProps : ViewSelectorDropdownPanelProps, dataStructure : DataStructure ) : boolean => {
    const forceFit   = viewPanelProps.forceFit || false,
          autoLayout = viewPanelProps.autoLayout || false;

    panelProps = extractViewPropsFromProps( panelProps as ReferencePanelComponentProps );
    viewPanelProps = extractViewPropsFromProps( viewPanelProps as ReferencePanelComponentProps );

    delete panelProps.search;
    delete viewPanelProps.search;
    delete panelProps.doSearch;
    delete viewPanelProps.doSearch;

    if ( !panelProps.rowStyling?.length ) {
        delete panelProps.rowStyling;
    }
    if ( !viewPanelProps.rowStyling?.length ) {
        delete viewPanelProps.rowStyling;
    }

    if ( dataStructure.dataStructure?.[ 0 ] ) {
        const parentReferenceColumnFields = getParentReferenceAttributeColumnDefinitions( dataStructure.dataStructure[ 0 ] ).map( colDef => colDef.field );

        let viewPanelPropsColumnsIncludesParentReferenceColumn : boolean = false;

        if ( viewPanelProps.columns ) {
            viewPanelProps.columns = [].concat( viewPanelProps.columns );

            // check if a parentReference column is included
            // remove column width if forceFit or autoLayout
            for ( let i = viewPanelProps.columns.length - 1; i >= 0; i-- ) {
                if ( parentReferenceColumnFields.includes( viewPanelProps.columns[ i ].field ) ) {
                    viewPanelPropsColumnsIncludesParentReferenceColumn = true;
                }

                if ( forceFit || autoLayout ) {
                    viewPanelProps.columns[ i ] = Object.assign( {}, viewPanelProps.columns[ i ] ) as ColumnConfig;
                    delete viewPanelProps.columns[ i ].width;
                }
            }
        }

        if ( panelProps.columns ) {
            panelProps.columns = [].concat( panelProps.columns );

            // remove parentReference columns
            // remove column width if forceFit or autoLayout
            for ( let i = panelProps.columns.length - 1; i >= 0; i-- ) {
                if ( !viewPanelPropsColumnsIncludesParentReferenceColumn && parentReferenceColumnFields.includes( panelProps.columns[ i ].field ) ) {
                    panelProps.columns.splice( i, 1 );
                }

                if ( forceFit || autoLayout ) {
                    panelProps.columns[ i ] = Object.assign( {}, panelProps.columns[ i ] ) as ColumnConfig;
                    delete panelProps.columns[ i ].width;
                }
            }
        }
    }

    return _.isEqual( panelProps, viewPanelProps );
}

/**
 * Remaps the field names in given objectArrayToRemap from Overview ones to ReferencePanel ones
 *
 * @param {any} objectArrayToRemap
 * @param {DataStructure} dataStructure
 *
 * @return {any}
 */
export const remapFieldsForReferenceView = ( objectArrayToRemap : any, dataStructure : DataStructure ) : any => {
    let retVal : any;

    if ( dataStructure.item.objectType ) {
        const search      = buildItemLabelForCypher( dataStructure.item.objectType ),
              replacement = getItemLabelForCypher( dataStructure ),
              aliasRegExp = new RegExp( '^' + search + '\\' + fieldSeparator ),
              fieldRegExp = new RegExp( '^' + search + '\\' + aliasSeparator );

        if ( Array.isArray( objectArrayToRemap ) ) {
            retVal = ( [] as any[] ).concat( objectArrayToRemap as any[] );

            for ( let i = 0; i < retVal.length; i++ ) {
                if ( typeof retVal[ i ] === 'object' ) {
                    retVal[ i ] = remapFieldsForReferenceView( retVal[ i ], dataStructure );
                }
            }
        }
        else if ( typeof objectArrayToRemap === 'object' ) {
            retVal = {
                ...objectArrayToRemap
            }

            const retValKeys = Object.keys( retVal );

            for ( const key of retValKeys ) {
                if ( key === 'alias' && retVal[ key ].match( aliasRegExp ) ) {
                    retVal[ key ] = retVal[ key ].replace( aliasRegExp, `${replacement}${fieldSeparator}` );
                }

                else if ( key === 'field' && retVal[ key ].match( fieldRegExp ) ) {
                    retVal[ key ] = retVal[ key ].replace( fieldRegExp, `${replacement}${aliasSeparator}` );
                }

                else if ( typeof retVal[ key ] === 'object' ) {
                    retVal[ key ] = remapFieldsForReferenceView( retVal[ key ], dataStructure );
                }
            }
        }
        else {
            retVal = objectArrayToRemap;
        }
    }

    return retVal;
}
